import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
    Button,
    Card,
    ConfigProvider,
    Empty,
    Modal,
    Popconfirm,
    Radio,
    Space,
    Table,
    TableProps,
    Tag,
    Tooltip,
    Typography
} from 'antd';
import locale from "antd/es/locale/es_ES";
import { AuthContext } from "../../../../config/AuthContext";
import JutgeColors from "../../../../styles/JutgeColors";
import { ColumnsType } from "antd/es/table";
import { GetJutgeActivitatIdProblemaIdTests } from "../../../../dao/jutge/GetJutgeActivitatIdProblemaIdTests";
import {
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    CheckCircleOutlined,
    CodeOutlined,
    ExperimentOutlined,
    FileTextOutlined,
    WarningOutlined,
    GlobalOutlined,
    AppstoreOutlined
} from "@ant-design/icons";
import { DeleteJutgeProblemaIdTestId } from "../../../../dao/jutge/DeleteJutgeProblemaIdTestId";
import TestFormModal from "./TestFormModal";

const { Text, Title } = Typography;

interface Props {
    id_problema: string;
    nom: string;
    id_activitat: string;
    visible: boolean;
    onClose: (value: boolean) => void;
    isMulti: boolean;
    readOnly?: boolean;
}

const TestsListModal: React.FC<Props> = (props: Props) => {
    const [loading, setLoading] = useState(false);
    const [dades, setDades] = useState<any[]>([]);
    const { isLoggedIn } = useContext(AuthContext);
    const [editVisible, setEditVisible] = useState(false);
    const [testEdit, setTestEdit] = useState<any>(null);
    const [filterType, setFilterType] = useState<string>("all");

    useEffect(() => {
        if (isLoggedIn && props.visible) {
            checkDades();
        }
    }, [props.visible]);

    async function checkDades() {
        setLoading(true);
        try {
            const tests = await GetJutgeActivitatIdProblemaIdTests(props.id_activitat + "", props.id_problema + "");
            setDades(tests || []);
        } finally {
            setLoading(false);
        }
    }

    const handleCancel = () => {
        props.onClose(false);
    };

    async function eliminar(element: any) {
        setLoading(true);
        try {
            // Un test E/S (o Mètode + E/S) són en realitat dues files: 'entrada' i 'sortida'.
            // Si esborrem una, esborrem també la seva parella.
            if (element.__pair) {
                const ids = [element.entradaRow?.id, element.sortidaRow?.id].filter(Boolean);
                for (const tid of ids) {
                    await DeleteJutgeProblemaIdTestId(element.id_problema + "", tid + "");
                }
            } else {
                await DeleteJutgeProblemaIdTestId(element.id_problema + "", element.id + "");
            }
            await checkDades();
        } finally {
            setLoading(false);
        }
    }

    const editar = (record: any) => {
        setTestEdit(record);
        setEditVisible(true);
    };

    const getTestTypeBadge = (tipus: string, record: any) => {
        switch (tipus) {
            case 'es':
                return <Tag color="blue" icon={<ExperimentOutlined />}>Entrada / Sortida</Tag>;
            case 'methodtest':
                return <Tag color="purple" icon={<CodeOutlined />}>Mètode + E/S</Tag>;
            case 'entrada':
                return <Tag color="blue" icon={<ExperimentOutlined />}>Entrada (I/O)</Tag>;
            case 'sortida':
                return <Tag color="green" icon={<CheckCircleOutlined />}>Sortida (I/O)</Tag>;
            case 'content':
                return <Tag color="cyan" icon={<FileTextOutlined />}>Codi Font</Tag>;
            case 'method':
                return <Tag color="purple" icon={<CodeOutlined />}>Mètode</Tag>;
            case 'class':
                return <Tag color="magenta" icon={<AppstoreOutlined />}>Estructura Classe</Tag>;
            case 'content_class':
                return <Tag color="geekblue" icon={<FileTextOutlined />}>Contingut Classe</Tag>;
            case 'exception_check':
                return <Tag color="volcano" icon={<WarningOutlined />}>Excepció</Tag>;
            case 'io_file':
                return <Tag color="gold" icon={<FileTextOutlined />}>Fitxer I/O</Tag>;
            case 'ui':
                return <Tag color="orange" icon={<GlobalOutlined />}>UI JSP</Tag>;
            default:
                return <Tag color="default">{tipus}</Tag>;
        }
    };

    const filteredDades = useMemo(() => {
        if (!dades) return [];
        let sorted = [...dades].sort((a: any, b: any) => a.id - b.id);
        if (filterType === "all") return sorted;
        if (filterType === "io") return sorted.filter(t => t.tipus === 'entrada' || t.tipus === 'sortida' || t.tipus === 'io_file');
        if (filterType === "code") return sorted.filter(t => t.tipus === 'method' || t.tipus === 'class' || t.tipus === 'content_class' || t.tipus === 'content');
        if (filterType === "other") return sorted.filter(t => !['entrada', 'sortida', 'io_file', 'method', 'class', 'content_class', 'content'].includes(t.tipus));
        return sorted;
    }, [dades, filterType]);

    // Decodifica el contingut d'una fila 'entrada'/'sortida' (format "PREFIX:...:base64" o base64 directe).
    const decodeIoText = (row: any): string => {
        if (!row || !row.fitxer) return "";
        try {
            const parts = String(row.fitxer).split(":");
            if (parts.length > 2) return atob(parts[2]);
            return atob(row.fitxer);
        } catch (e) {
            return String(row.fitxer);
        }
    };

    // Agrupa les files 'entrada' + 'sortida' que comparteixen 'ordre' en una sola fila lògica.
    // Es considera "Mètode + E/S" (methodtest) si la fila té methodname; si no, és E/S simple.
    const groupedDades = useMemo(() => {
        const rows = filteredDades || [];
        const result: any[] = [];
        const pairs = new Map<string, any>();

        for (const t of rows) {
            if (t.tipus === 'entrada' || t.tipus === 'sortida') {
                const isMethod = !!t.methodname;
                const key = `${isMethod ? 'm' : 'es'}-${t.ordre}-${t.methodname || ''}`;
                let pair = pairs.get(key);
                if (!pair) {
                    pair = {
                        __pair: true,
                        tipus: isMethod ? 'methodtest' : 'es',
                        ordre: t.ordre,
                        id_problema: t.id_problema,
                        methodname: t.methodname || null,
                        entradaRow: null,
                        sortidaRow: null,
                    };
                    pairs.set(key, pair);
                    result.push(pair);
                }
                if (t.tipus === 'entrada') pair.entradaRow = t;
                else pair.sortidaRow = t;
            } else {
                result.push(t);
            }
        }

        // id/key estable per la fila agrupada (fem servir el menor id de la parella).
        return result.map(r => {
            if (r.__pair) {
                const eid = r.entradaRow?.id;
                const sid = r.sortidaRow?.id;
                r.id = [eid, sid].filter((x) => x != null).sort((a: number, b: number) => a - b)[0];
                r.rowKey = `pair-${r.tipus}-${r.ordre}-${r.methodname || ''}`;
            } else {
                r.rowKey = `t-${r.id}`;
            }
            return r;
        });
    }, [filteredDades]);

    const columns: ColumnsType<any> = [
        {
            key: 'id',
            title: '#',
            dataIndex: 'id',
            width: 55,
            align: 'center',
            render: (id: any) => (
                <span style={{ fontWeight: 600, color: '#8c8c8c' }}>{id}</span>
            )
        },
        {
            key: 'tipus',
            title: 'Tipus de Validació',
            dataIndex: 'tipus',
            width: 170,
            render: (value: string, record: any) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {getTestTypeBadge(value, record)}
                    {record?.methodname && (
                        <Tag color="geekblue" style={{ fontSize: '0.75rem', width: 'fit-content' }}>
                            {record.methodname}
                        </Tag>
                    )}
                </div>
            )
        },
        {
            key: 'fitxer',
            title: 'Configuració i Criteri de Validació',
            dataIndex: 'fitxer',
            render: (value: string, record: any) => {
                // Fila agrupada E/S o Mètode + E/S: dues columnes (Entrada | Sortida).
                if (record.__pair) {
                    const entrada = decodeIoText(record.entradaRow);
                    const sortida = decodeIoText(record.sortidaRow);
                    const cellStyle: React.CSSProperties = {
                        margin: 0,
                        padding: '6px 8px',
                        borderRadius: 4,
                        fontFamily: 'monospace',
                        fontSize: '0.82rem',
                        maxHeight: 110,
                        overflow: 'auto',
                        whiteSpace: 'pre-wrap'
                    };
                    return (
                        <div style={{ display: 'flex', gap: 8 }}>
                            <div style={{ flex: 1 }}>
                                <Text type="secondary" style={{ fontSize: '0.72rem' }}>ENTRADA</Text>
                                <pre style={{ ...cellStyle, background: '#f0f5ff' }}>
                                    {entrada || "(Sense entrada)"}
                                </pre>
                            </div>
                            <div style={{ flex: 1 }}>
                                <Text type="secondary" style={{ fontSize: '0.72rem' }}>SORTIDA ESPERADA</Text>
                                <pre style={{ ...cellStyle, background: '#f6ffed' }}>
                                    {sortida || "(Buida)"}
                                </pre>
                            </div>
                        </div>
                    );
                }
                try {
                    let decoded = "";
                    let isJson = false;

                    try {
                        const directJson = JSON.parse(value);
                        if (directJson && typeof directJson === 'object') {
                            decoded = value;
                            isJson = true;
                        }
                    } catch (e) { }

                    if (!isJson && value.includes(":")) {
                        const parts = value.split(":");
                        if (parts.length > 2) decoded = atob(parts[2]);
                    }

                    if (!decoded) {
                        return (
                            <div style={{
                                maxHeight: '110px',
                                overflow: 'auto',
                                fontFamily: 'monospace',
                                fontSize: '0.82rem',
                                background: '#f5f5f5',
                                padding: '6px 8px',
                                borderRadius: 4
                            }}>
                                {value}
                            </div>
                        );
                    }

                    if (record.tipus === "method") {
                        const methodData = JSON.parse(decoded);
                        return (
                            <div style={{ fontSize: '0.85rem' }}>
                                <div style={{ marginBottom: 4 }}>
                                    <Text strong>Mètode:</Text> <Tag color="purple">{methodData.methodName}</Tag>
                                    <Text strong style={{ marginLeft: 8 }}>Retorn:</Text> <Tag color="cyan">{methodData.returnType}</Tag>
                                </div>
                                <div style={{ marginBottom: 4 }}>
                                    <Text type="secondary">Modificadors: </Text>
                                    {methodData.modifiers && methodData.modifiers.length > 0 ? (
                                        methodData.modifiers.map((m: string, idx: number) => (
                                            <Tag key={idx} color="purple">{m}</Tag>
                                        ))
                                    ) : (
                                        <Tag color="default">Default</Tag>
                                    )}
                                </div>
                                <div>
                                    <Text type="secondary">Paràmetres: </Text>
                                    {methodData.parameters && methodData.parameters.length > 0 ? (
                                        methodData.parameters.map((p: any, idx: number) => (
                                            <Tag key={idx} color="blue">
                                                {p.paramType} {p.paramName}
                                            </Tag>
                                        ))
                                    ) : (
                                        <Tag color="default">Sense paràmetres</Tag>
                                    )}
                                </div>
                            </div>
                        );
                    }

                    if (record.tipus === "class") {
                        const classData = JSON.parse(decoded);
                        return (
                            <div style={{ fontSize: '0.84rem' }}>
                                <div style={{ marginBottom: 6 }}>
                                    {classData.modifiers && classData.modifiers.map((m: string, idx: number) => (
                                        <Tag key={idx} color="purple">{m}</Tag>
                                    ))}
                                    <Tag color="magenta">{classData.classType || "class"}</Tag>
                                    <Text strong style={{ color: '#1890ff', fontSize: '0.95rem' }}>{classData.className}</Text>
                                    {classData.extendedClass && (
                                        <span style={{ marginLeft: 6 }}>
                                            <Text type="secondary">extends</Text> <Tag color="orange">{classData.extendedClass}</Tag>
                                        </span>
                                    )}
                                </div>

                                {classData.constructors && classData.constructors.length > 0 && (
                                    <div style={{ marginBottom: 4 }}>
                                        <Text type="secondary" style={{ fontSize: '0.78rem' }}>CONSTRUCTORS ({classData.constructors.length}):</Text>
                                        <ul style={{ paddingLeft: 18, margin: 0 }}>
                                            {classData.constructors.map((c: any, idx: number) => (
                                                <li key={idx} style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                    {c.modifiers?.join(' ')} {classData.className}(
                                                    {c.parameters?.map((p: any) => `${p.paramType} ${p.paramName}`).join(', ')}
                                                    )
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {classData.fields && classData.fields.length > 0 && (
                                    <div style={{ marginBottom: 4 }}>
                                        <Text type="secondary" style={{ fontSize: '0.78rem' }}>ATRIBUTS ({classData.fields.length}):</Text>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                                            {classData.fields.map((f: any, idx: number) => (
                                                <Tag key={idx} color="blue">
                                                    {f.fieldType} <b>{f.fieldName}</b>
                                                    {f.checkGetter && <span style={{ marginLeft: 3, color: '#52c41a' }}>[G]</span>}
                                                    {f.checkSetter && <span style={{ marginLeft: 2, color: '#1890ff' }}>[S]</span>}
                                                </Tag>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {classData.methods && classData.methods.length > 0 && (
                                    <div>
                                        <Text type="secondary" style={{ fontSize: '0.78rem' }}>MÈTODES ({classData.methods.length}):</Text>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}>
                                            {classData.methods.map((m: any, idx: number) => (
                                                <Tag key={idx} color="purple">
                                                    {m.returnType} <b>{m.methodName}</b>(...)
                                                </Tag>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    }

                    if (record.tipus === "content_class") {
                        const contentData = JSON.parse(decoded);
                        return (
                            <div style={{ fontSize: '0.85rem' }}>
                                <div><Text strong>Classe de referència:</Text> <Tag color="geekblue">{contentData.refClassName || "Unknown"}</Tag></div>
                                <div style={{ marginTop: 4 }}><Text strong>Cerca:</Text> <Tag style={{ fontFamily: 'monospace' }}>{contentData.searchText}</Tag></div>
                                <div style={{ marginTop: 4 }}>
                                    <Text strong>Regla:</Text> {contentData.mustContain ? (
                                        <Tag color="success">Ha de contenir</Tag>
                                    ) : (
                                        <Tag color="error">No ha de contenir</Tag>
                                    )}
                                </div>
                            </div>
                        );
                    }

                    if (record.tipus === "exception_check") {
                        const excData = JSON.parse(decoded);
                        return (
                            <div style={{ fontSize: '0.85rem' }}>
                                <div><Text strong>Mètode objectiu:</Text> <Tag color="geekblue">{excData.refClassName || "?"}.{excData.methodName}</Tag></div>
                                <div style={{ marginTop: 4 }}><Text strong>Excepció esperada:</Text> <Tag color="volcano">{excData.exceptionType}</Tag></div>
                                {excData.expectedOutput && (
                                    <div style={{ marginTop: 4 }}>
                                        <Text strong>Missatge esperat:</Text>
                                        <pre style={{ margin: '4px 0', padding: '4px 8px', background: '#fff2e8', borderRadius: 4, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                                            {excData.expectedOutput}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        );
                    }

                    if (record.tipus === "io_file") {
                        const ioData = JSON.parse(decoded);
                        let decodedInput = "";
                        if (ioData.input) {
                            try {
                                decodedInput = decodeURIComponent(escape(atob(ioData.input)));
                            } catch (e) {
                                try {
                                    decodedInput = atob(ioData.input);
                                } catch (err) {
                                    decodedInput = ioData.input;
                                }
                            }
                        }
                        return (
                            <div style={{ fontSize: '0.85rem' }}>
                                <div><Text strong>Fitxer associat:</Text> <Tag color="gold">{ioData.fileName || "datos.txt"}</Tag></div>
                                <div style={{ marginTop: 4 }}>
                                    <Text strong>Contingut fitxer d'entrada:</Text>
                                    <pre style={{ margin: '4px 0', padding: '4px 8px', background: '#f5f5f5', borderRadius: 4, fontSize: '0.8rem', maxHeight: 80, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                        {decodedInput || "(Buit)"}
                                    </pre>
                                </div>
                                <div>
                                    <Text strong>Sortida esperada:</Text>
                                    <pre style={{ margin: '4px 0', padding: '4px 8px', background: '#f6ffed', borderRadius: 4, fontSize: '0.8rem', maxHeight: 80, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                        {ioData.output || ioData.outputContent || "(Sense sortida)"}
                                    </pre>
                                </div>
                            </div>
                        );
                    }

                    if (record.tipus === "ui") {
                        const uiData = JSON.parse(decoded);
                        return (
                            <div style={{ fontSize: '0.85rem' }}>
                                <div><Text strong>Fitxer JSP:</Text> <Tag color="geekblue">{uiData.jspFile || "index.jsp"}</Tag></div>
                                {uiData.requestParams && <div style={{ marginTop: 4 }}><Text strong>Paràmetres:</Text> <Tag>{uiData.requestParams}</Tag></div>}
                                <div style={{ marginTop: 4 }}>
                                    <Text strong>Sortida esperada HTML:</Text>
                                    <pre style={{ margin: '4px 0', padding: '4px 8px', background: '#fffbe6', borderRadius: 4, fontSize: '0.8rem', maxHeight: 80, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                                        {uiData.expectedOutput || "(Buit)"}
                                    </pre>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <pre style={{ whiteSpace: "pre-wrap", maxHeight: 120, overflow: "auto", background: '#fafafa', padding: 6, borderRadius: 4, fontSize: '0.82rem', margin: 0 }}>
                            {decoded}
                        </pre>
                    );
                } catch (err) {
                    return <Tag color="error">Error al descodificar prova</Tag>;
                }
            }
        },
    ];

    if (!props.readOnly) {
        columns.push({
            key: 'actions',
            title: 'Accions',
            dataIndex: 'tipus',
            width: 90,
            align: 'center',
            render: (value: string, element) => (
                <Space size={6}>
                    <Tooltip title="Editar Test">
                        <Button
                            type="text"
                            size="small"
                            icon={<EditOutlined style={{ color: '#1890ff' }} />}
                            onClick={() => editar(element)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Eliminar test"
                        description={element.__pair
                            ? "Això eliminarà l'entrada i la sortida d'aquest test. Continuar?"
                            : "Segur que vols eliminar aquesta prova?"}
                        okText="Sí, eliminar"
                        cancelText="Cancel·lar"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => eliminar(element)}
                    >
                        <Tooltip title="Eliminar Test">
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        });
    }

    const tableProps: TableProps<any> = { loading };

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                backgroundColor: '#f0f5ff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#2f54eb',
                                fontSize: 18
                            }}>
                                <ExperimentOutlined />
                            </div>
                            <div>
                                <Title level={5} style={{ margin: 0, fontWeight: 600 }}>
                                    Bateria de Tests: <span style={{ color: '#1890ff' }}>{props.nom}</span>
                                </Title>
                                <Text type="secondary" style={{ fontSize: '0.82rem' }}>
                                    Gestiona les proves d'avaluació automàtica per aquest problema
                                </Text>
                            </div>
                        </div>

                        {!props.readOnly && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => {
                                    setTestEdit(null);
                                    setEditVisible(true);
                                }}
                                style={{ backgroundColor: JutgeColors.colors.color4, borderColor: JutgeColors.colors.color4 }}
                            >
                                Nou Test
                            </Button>
                        )}
                    </div>
                }
                open={props.visible}
                onCancel={handleCancel}
                footer={[
                    <Button key="close" onClick={handleCancel}>
                        Tancar
                    </Button>
                ]}
                width={980}
                style={{ top: 20 }}
            >
                {/* Barra de filtre ràpid i resum */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 14,
                    padding: '8px 12px',
                    background: '#fafafa',
                    borderRadius: 8,
                    border: '1px solid #f0f0f0'
                }}>
                    <Space size={8}>
                        <Text strong style={{ fontSize: '0.85rem' }}>Filtra per tipus:</Text>
                        <Radio.Group
                            size="small"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            buttonStyle="solid"
                        >
                            <Radio.Button value="all">Tots ({dades?.length || 0})</Radio.Button>
                            <Radio.Button value="io">Entrada / Sortida</Radio.Button>
                            <Radio.Button value="code">Mètodes & Classes</Radio.Button>
                            <Radio.Button value="other">Altres</Radio.Button>
                        </Radio.Group>
                    </Space>

                    <div>
                        <Tag color="blue" style={{ margin: 0 }}>
                            {groupedDades.length} proves mostrades
                        </Tag>
                    </div>
                </div>

                <Table
                    size="middle"
                    {...tableProps}
                    bordered
                    columns={columns}
                    dataSource={groupedDades}
                    rowKey="rowKey"
                    pagination={{ pageSize: 8, showSizeChanger: true }}
                    locale={{
                        emptyText: <Empty description="Aquest problema encara no té tests configurats" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    }}
                />
            </Modal>

            <TestFormModal
                visible={editVisible}
                id_activitat={props.id_activitat}
                id_problema={props.id_problema}
                testData={testEdit}
                onClose={setEditVisible}
                onSave={checkDades}
                allTests={dades}
                isMulti={props.isMulti}
            />
        </ConfigProvider>
    );
};

export default TestsListModal;
